import React, { useState, useEffect, useMemo } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  Avatar,
  Box,
  IconButton,
  Typography,
  Tooltip,
  Button,
  Switch,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { createAppTheme } from './theme';
import config from './config';
import { auth } from './services/auth';
import Login from './components/Login';
import ProjectsNetworkStatusTab from './components/ProjectsNetworkStatusTab';
import MonitoringDashboard from './components/MonitoringDashboard';
import { useDemoMode } from './hooks/useDemoMode';
import { UI_VERSION, BUILD_TIME_ISO } from './version';
import './App.css';

const SIDEBAR_WIDTH = 240;

const PAGES = [
  { label: 'Overview', description: 'Fleet health at a glance', icon: DashboardOutlinedIcon },
  { label: 'Projects', description: 'Network and device health by project', icon: GridViewOutlinedIcon },
  { label: 'Monitoring', description: 'Alerts and alerting policies', icon: NotificationsNoneOutlinedIcon },
];

// Separate from the legacy 'theme' key, which defaulted everyone to the old dark UI.
const COLOR_MODE_KEY = 'pm-color-mode';

const ENV_LABELS = { dev: 'Development', staging: 'Staging', prod: 'Production' };

const getInitials = (name) =>
  String(name)
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

/**
 * App Component - Minimal entry point for Projects Status View
 * 
 * This is a standalone read-only application that displays global project network status.
 * It uses the same backend connection logic and UI components as the main network manager UI.
 * 
 * Features:
 * - Global view of all projects' status
 * - Three view modes: Cards, Map, and List
 * - Project details dialog for deep dive into specific projects
 * - Real-time data fetching from backend API
 * - Built-in login page
 */
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return auth.isAuthenticated();
  });
  const [userInfo, setUserInfo] = useState(() => {
    return auth.getUserInfo();
  });
  const [page, setPage] = useState(0); // 0 = Overview, 1 = Projects, 2 = Monitoring
  const [demoModeEnabled, setDemoModeEnabled] = useState(false);
  const demoMode = useDemoMode();
  const [colorMode, setColorMode] = useState(() => (localStorage.getItem(COLOR_MODE_KEY) === 'dark' ? 'dark' : 'light'));
  const theme = useMemo(() => createAppTheme(colorMode), [colorMode]);
  const isWide = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    localStorage.setItem(COLOR_MODE_KEY, colorMode);
  }, [colorMode]);

  const toggleColorMode = () => setColorMode((prev) => (prev === 'dark' ? 'light' : 'dark'));

  // Check authentication on mount and when token changes.
  // Refresh the login token before it expires so a short-lived token does not force a new login.
  useEffect(() => {
    let cancelled = false;
    const checkAuth = async () => {
      const token = await auth.ensureFreshToken();
      if (cancelled) return;
      if (!token) {
        setIsAuthenticated(false);
        setUserInfo(null);
        if (localStorage.getItem('token')) auth.logout();
        return;
      }
      setIsAuthenticated(true);
      setUserInfo(auth.getUserInfo());
      auth.scheduleRefresh();
    };

    checkAuth();

    window.addEventListener('auth:unauthorized', checkAuth);
    const interval = setInterval(checkAuth, 30000);

    return () => {
      cancelled = true;
      window.removeEventListener('auth:unauthorized', checkAuth);
      clearInterval(interval);
    };
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setUserInfo(auth.getUserInfo());
  };

  // Register page setter for demo mode
  useEffect(() => {
    if (demoMode) {
      demoMode.registerControls('app', { setPage });
    }
  }, [demoMode]);

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  const activePage = PAGES[page];
  const envLabel = ENV_LABELS[config.getEnvironment()] || config.getEnvironment();
  const displayName = userInfo?.name || userInfo?.email || 'User';

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        <Box
          component="nav"
          sx={{
            width: { xs: 64, md: SIDEBAR_WIDTH },
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            borderRight: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: { xs: 1.5, md: 2.5 }, height: 64 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                bgcolor: 'grey.100',
                color: 'grey.800',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <HubOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.2 }}>
                Monitoring
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 }}>
                {envLabel}
              </Typography>
            </Box>
          </Box>

          <List sx={{ px: { xs: 1, md: 1.5 }, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {PAGES.map((p, index) => {
              const Icon = p.icon;
              const selected = page === index;
              return (
                <Tooltip key={p.label} title={p.label} placement="right" disableHoverListener={isWide}>
                  <ListItemButton
                    selected={selected}
                    onClick={() => setPage(index)}
                    sx={{
                      borderRadius: 1.5,
                      minHeight: 38,
                      px: 1.25,
                      justifyContent: { xs: 'center', md: 'flex-start' },
                      color: selected ? 'text.primary' : 'text.secondary',
                      '&.Mui-selected': { bgcolor: 'grey.100' },
                      '&.Mui-selected:hover': { bgcolor: 'grey.200' },
                      '&:hover': { bgcolor: 'grey.50', color: 'text.primary' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: { xs: 0, md: 34 }, color: 'inherit' }}>
                      <Icon sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={p.label}
                      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: selected ? 600 : 500 }}
                      sx={{ display: { xs: 'none', md: 'block' }, m: 0 }}
                    />
                  </ListItemButton>
                </Tooltip>
              );
            })}
          </List>

          <Box sx={{ flex: 1 }} />

          <Box sx={{ px: { xs: 1, md: 2 }, py: 1.5, borderTop: 1, borderColor: 'divider', display: { xs: 'none', md: 'block' } }}>
            <FormControlLabel
              control={
                <Switch
                  checked={demoModeEnabled}
                  onChange={(e) => setDemoModeEnabled(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>Demo mode</Typography>}
              sx={{ m: 0, gap: 1 }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: { xs: 0.5, md: 0.5 },
              px: { xs: 1, md: 2 },
              py: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}
          >
            <Avatar sx={{ width: 30, height: 30, fontSize: '0.75rem', fontWeight: 600, bgcolor: 'grey.200', color: 'grey.800', display: { xs: 'none', md: 'flex' } }}>
              {getInitials(displayName)}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1, ml: 0.75, display: { xs: 'none', md: 'block' } }}>
              <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.3 }}>
                {displayName}
              </Typography>
              <Tooltip title={`Built ${BUILD_TIME_ISO}`}>
                <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', lineHeight: 1.3 }}>
                  v{UI_VERSION}
                </Typography>
              </Tooltip>
            </Box>
            <Tooltip title={colorMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              <IconButton
                onClick={toggleColorMode}
                size="small"
                aria-label="Toggle color mode"
                sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}
              >
                {colorMode === 'dark' ? <LightModeOutlinedIcon sx={{ fontSize: 18 }} /> : <DarkModeOutlinedIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Sign out">
              <IconButton
                onClick={() => auth.logout()}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}
              >
                <LogoutIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 4 }, pb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 3 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography component="h1" sx={{ fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.25 }}>
                  {activePage.label}
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 0.5 }}>
                  {activePage.description}
                </Typography>
              </Box>
              {demoModeEnabled && (
                !demoMode.isActive ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => {
                      // Get projects list from network data - will be passed from child
                      const projects = window.__demoProjects || [];
                      demoMode.startDemo(projects);
                    }}
                    disabled={!window.__demoProjects || window.__demoProjects.length === 0}
                  >
                    Start demo
                  </Button>
                ) : (
                  <Button size="small" variant="outlined" startIcon={<StopIcon />} onClick={demoMode.stopDemo}>
                    Stop demo
                  </Button>
                )
              )}
            </Box>

            {page === 2 ? (
              <MonitoringDashboard demoMode={demoModeEnabled ? demoMode : null} />
            ) : (
              <ProjectsNetworkStatusTab
                isActive={true}
                section={page === 0 ? 'overview' : 'projects'}
                onOpenProjects={() => setPage(1)}
                demoMode={demoModeEnabled ? demoMode : null}
              />
            )}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
